// ============================================================
// ADD MEMBER MODAL — ported from the known-working CLEAN-15 version.
//
// Key invariants preserved from the working version:
//  - voterSearch is NEVER cleared programmatically after selecting a voter.
//    searchResults therefore stays populated while the modal is open, and
//    voterId remains a simple state value tied to a <select>.
//  - At save time, voter_id is taken directly from voterId state (parseInt),
//    so no cross-state coordination is needed.
//  - member insert is always done here in ONE place (no split paths).
//  - onSave(famId, member) is still called so the parent can (a) create a
//    family on the fly if needed, and (b) refresh its local caches.
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { FAMILY_ROLES } from './shared/constants';
import { uploadPhoto, deletePhoto } from './shared/photoStorage';
import { usePhotoUrl } from './shared/usePhotoUrl';
import { Icon } from './shared/Icon';

const AddMemberModal2Step = ({ familyId, families, voter, onClose, onSave, supabase, voters, relatives }) => {
  // `relatives` is the list of people the new member can be linked to
  // (parents picker + spouse picker). It comes from the viewer's tree view
  // (treeFamily.members) so the modal looks the same whether the viewer is
  // a blood-rooted person or a married-in spouse with their own small tree.
  // Falls back to the family-by-id lookup for legacy callers.
  const family = families.find(f => f.id === familyId);
  const members = relatives && relatives.length > 0 ? relatives : (family?.members || []);

  // Pre-generate the new member's id ONCE when the modal mounts. We need
  // a stable id before the user picks a photo, because Storage uploads
  // are keyed by the member id (so the file lands at the right path and
  // re-uploads overwrite cleanly).
  const newMemberId = useMemo(() => 'm' + Date.now(), []);

  const [role, setRole] = useState('');
  const [voterId, setVoterId] = useState('');        // string; the <select>'s value
  const [voterSearch, setVoterSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [extName, setExtName] = useState('');

  // OPTIONAL relationship fields (kept from the 2-step version)
  const [parentIds, setParentIds] = useState([]);
  const [spouseId, setSpouseId] = useState('');

  // Manual member photo. Stored in state as a Storage path (after upload).
  // Re-resolved to a signed URL via usePhotoUrl for the preview.
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const photoPreview = usePhotoUrl(supabase, photoUrl);

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoError('');
    setPhotoUploading(true);
    try {
      const path = await uploadPhoto(supabase, file, 'family_members', newMemberId);
      setPhotoUrl(path);
    } catch (err) {
      console.error('AddMember photo upload failed:', err);
      setPhotoError(err.message || 'Upload failed');
    } finally {
      setPhotoUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemovePhoto = () => {
    if (photoUrl) deletePhoto(supabase, photoUrl);
    setPhotoUrl('');
  };

  // If the user uploaded a photo and then closes the modal without saving,
  // the file sits orphaned in Storage. Best-effort delete on close.
  const handleClose = () => {
    if (photoUrl) deletePhoto(supabase, photoUrl);
    onClose();
  };

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Live voter search (no debounce — Supabase ilike is fast enough).
  useEffect(() => {
    if (!voterSearch.trim()) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    supabase
      .from('voters')
      .select('id,name,island,house,id_number')
      .ilike('name', `%${voterSearch.trim()}%`)
      .order('name')
      .limit(100)
      .then(({ data, error: sErr }) => {
        if (cancelled) return;
        if (sErr) {
          console.error('Voter search error:', sErr);
          setSearchResults([]);
        } else {
          setSearchResults(data || []);
        }
        setSearching(false);
      });
    return () => { cancelled = true; };
  }, [voterSearch, supabase]);

  const toggleParent = (id) => {
    setParentIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length < 2) return [...prev, id];
      return prev;
    });
  };

  // Derived: the currently-selected voter (from searchResults).
  // Matches CLEAN-15's save-time lookup so nothing can go stale.
  const pickedVoter = voterId
    ? searchResults.find(v => v.id === parseInt(voterId, 10)) || null
    : null;

  const save = async () => {
    if (saving) return;
    setError('');

    if (!role) {
      setError('Please select a role.');
      return;
    }

    // Figure out the member name.
    let memberName = extName.trim();
    if (voterId) {
      if (pickedVoter) {
        memberName = pickedVoter.name;
      } else {
        // Shouldn't happen — voterId is always set from a search result —
        // but guard just in case the list was re-fetched.
        setError('Selected voter is no longer in the search results. Please re-select.');
        return;
      }
    }

    if (!memberName) {
      setError('Please enter a name or link to a voter.');
      return;
    }

    setSaving(true);

    // For voter-linked members the photo goes on voters.photo_url so it shows
    // both on the voter profile and (via fallback) in the family tree. For
    // manual entries the photo stays on family_members.photo_url.
    const parsedVoterId = voterId ? parseInt(voterId, 10) : null;
    const photoForVoter = parsedVoterId && photoUrl ? photoUrl : null;
    const photoForMember = parsedVoterId ? null : (photoUrl || null);

    const member = {
      id: newMemberId,
      family_id: familyId || 'temp',  // parent will rewrite if it has to create the family
      role,
      voter_id: parsedVoterId,
      name: memberName,
      parent_ids: parentIds,
      spouse_id: spouseId || null,
      photo_url: photoForMember,
    };

    if (photoForVoter) {
      await supabase
        .from('voters')
        .update({ photo_url: photoForVoter })
        .eq('id', parsedVoterId);
    }

    // If the family already exists, insert here. Otherwise let the parent
    // (addMemberToFamily) create the family AND do the insert — it has to
    // set member.family_id to the new id, so we MUST NOT insert first in
    // that case or we'd end up with family_id = 'temp' in the DB.
    if (familyId) {
      const { error: insertErr } = await supabase.from('family_members').insert(member);
      if (insertErr) {
        setError('Error: ' + insertErr.message);
        setSaving(false);
        return;
      }
      if (parsedVoterId) {
        await supabase
          .from('voters')
          .update({ family_id: familyId })
          .eq('id', parsedVoterId);
      }
    }

    try {
      await onSave(familyId, member);
    } catch (e) {
      console.error('onSave failed:', e);
      setError('Saved the member, but refresh failed. Please reload.');
      setSaving(false);
      return;
    }

    setSaving(false);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.9)',
      zIndex: 1100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16
    }}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        maxWidth: 600,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: 20,
            color: 'var(--text-strong)'
          }}>
            Add Family Member
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-3)',
              fontSize: 22,
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {/* Role */}
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, fontFamily: 'DM Mono, monospace' }}>
            Role in family
          </div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--text-strong)',
              fontSize: 14,
              fontFamily: 'DM Mono, monospace',
              marginBottom: 20
            }}
          >
            <option value="">-- Select a role --</option>
            {FAMILY_ROLES.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>

          {role && (
            <>
              {/* Voter linking — PROVEN CLEAN-15 PATTERN */}
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, fontFamily: 'DM Mono, monospace' }}>
                Link to existing voter (optional)
              </div>
              <input
                type="text"
                value={voterSearch}
                onChange={(e) => setVoterSearch(e.target.value)}
                placeholder="Search voter by name..."
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  color: 'var(--text-strong)',
                  fontSize: 14,
                  fontFamily: 'DM Mono, monospace',
                  marginBottom: 8
                }}
              />
              {searching && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 8 }}>
                  Searching…
                </div>
              )}
              {searchResults.length > 0 && (
                <select
                  value={voterId}
                  onChange={(e) => setVoterId(e.target.value)}
                  size={Math.min(5, searchResults.length + 1)}
                  style={{
                    width: '100%',
                    padding: 8,
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    color: 'var(--text-strong)',
                    fontSize: 13,
                    fontFamily: 'DM Mono, monospace',
                    marginBottom: 8
                  }}
                >
                  <option value="">-- Select voter --</option>
                  {searchResults.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.island}{v.house ? ', ' + v.house : ''}{v.id_number ? ', ' + v.id_number : ''})
                    </option>
                  ))}
                </select>
              )}
              {pickedVoter && (
                <div style={{
                  padding: 10,
                  background: 'var(--sup-strong-bg)',
                  border: '1px solid var(--sup-strong-dot)',
                  borderRadius: 6,
                  marginBottom: 16
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--sup-strong-text)', fontFamily: 'DM Mono, monospace' }}>
                    <Icon name="check" size={12} /> Will link to: <b>{pickedVoter.name}</b>
                  </div>
                </div>
              )}

              {/* Manual name — disabled if a voter is picked */}
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, marginTop: 8, fontFamily: 'DM Mono, monospace' }}>
                Or enter name manually
              </div>
              <input
                type="text"
                value={extName}
                onChange={(e) => setExtName(e.target.value)}
                placeholder="Enter name..."
                disabled={!!voterId}
                style={{
                  width: '100%',
                  padding: 12,
                  background: voterId ? 'var(--surface)' : 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  color: voterId ? 'var(--text-3)' : 'var(--text-strong)',
                  fontSize: 14,
                  fontFamily: 'DM Mono, monospace',
                  marginBottom: 20
                }}
              />

              {/* Photo upload — works for manual entries and as an override
                  for voter-linked members. Same UX as voter profile photo. */}
              <>
                  <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, fontFamily: 'DM Mono, monospace' }}>
                    {voterId ? "Photo (optional — overrides voter's profile photo in tree)" : 'Photo (optional)'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: '50%',
                      border: '1px solid var(--border)',
                      background: 'var(--surface-2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'var(--text-4)', fontSize: 22, fontFamily: "'Cormorant Garamond', serif",
                      flexShrink: 0,
                      opacity: photoUploading ? 0.5 : 1,
                      overflow: 'hidden'
                    }}>
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt=""
                          style={{
                            width: '100%', height: '100%',
                            objectFit: 'cover', objectPosition: 'center',
                            display: 'block'
                          }}
                        />
                      ) : (
                        extName.trim() ? extName.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() : '?'
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{
                        padding: '6px 12px',
                        background: photoUploading ? 'var(--surface-2)' : 'var(--border)',
                        border: '1px solid var(--border)',
                        borderRadius: 5,
                        color: photoUploading ? 'var(--text-4)' : 'var(--link)',
                        cursor: photoUploading ? 'wait' : 'pointer',
                        fontSize: 11,
                        fontFamily: 'DM Mono, monospace',
                        textAlign: 'center'
                      }}>
                        {photoUploading ? 'Uploading…' : (photoUrl ? 'Change photo' : 'Upload photo')}
                        <input
                          type="file"
                          accept="image/*"
                          disabled={photoUploading}
                          onChange={handlePhoto}
                          style={{ display: 'none' }}
                        />
                      </label>
                      {photoUrl && !photoUploading && (
                        <button
                          type="button"
                          onClick={handleRemovePhoto}
                          style={{
                            padding: '6px 12px',
                            background: 'rgba(239,68,68,0.12)',
                            border: '1px solid rgba(239,68,68,0.5)',
                            borderRadius: 5,
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: 11,
                            fontFamily: 'DM Mono, monospace'
                          }}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  {photoError && (
                    <div style={{ fontSize: 10, color: '#ef4444', marginBottom: 16 }}>
                      {photoError}
                    </div>
                  )}
                  {!photoError && <div style={{ marginBottom: 12 }} />}
                </>

              {/* Optional parents */}
              {members.length > 0 && (
                <>
                  <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, fontFamily: 'DM Mono, monospace' }}>
                    Parents (optional, up to 2)
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                    {members.map(m => {
                      const selected = parentIds.includes(m.id);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => toggleParent(m.id)}
                          style={{
                            padding: '8px 14px',
                            borderRadius: 6,
                            border: selected ? '2px solid #3b82f6' : '1px solid var(--border)',
                            background: selected ? 'var(--border)' : 'transparent',
                            color: selected ? 'var(--link)' : 'var(--text-2)',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontFamily: 'DM Mono, monospace'
                          }}
                        >
                          {(m.name || '?').slice(0, 14)} ({m.role})
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Optional spouse */}
              {members.length > 0 && (
                <>
                  <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 8, fontFamily: 'DM Mono, monospace' }}>
                    Spouse (optional)
                  </div>
                  <select
                    value={spouseId}
                    onChange={(e) => setSpouseId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 12,
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      color: 'var(--text-strong)',
                      fontSize: 14,
                      fontFamily: 'DM Mono, monospace',
                      marginBottom: 20
                    }}
                  >
                    <option value="">— None —</option>
                    {members.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.name || '?'} ({m.role})
                      </option>
                    ))}
                  </select>
                </>
              )}

              {error && (
                <div style={{
                  padding: 12,
                  borderRadius: 6,
                  background: 'rgba(239,68,68,0.12)',
                  border: '1px solid rgba(239,68,68,0.4)',
                  color: '#ef4444',
                  fontSize: 12,
                  fontFamily: 'DM Mono, monospace',
                  marginBottom: 12
                }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {role && (
          <div style={{
            padding: '14px 24px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: 12,
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text-2)',
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'DM Mono, monospace'
              }}
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || (!voterId && !extName.trim())}
              style={{
                padding: '10px 20px',
                background: saving || (!voterId && !extName.trim()) ? 'var(--surface-3)' : '#3b82f6',
                border: 'none',
                borderRadius: 6,
                color: '#fff',
                cursor: saving || (!voterId && !extName.trim()) ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontFamily: 'DM Mono, monospace'
              }}
            >
              {saving ? 'Adding…' : 'Add to Tree'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddMemberModal2Step;
